import React, { useState, useEffect } from 'react';
import { Save, Settings as SettingsIcon, Clock, Calendar } from 'lucide-react';
import { supabase } from '../../../lib/supabase';

interface Setting {
  setting_key: string;
  setting_value: any;
}

const CalendarSettings = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({
    workingHours: {
      monday: { enabled: true, start: '09:00', end: '14:00', break_start: '14:00', break_end: '16:00', evening_start: '16:00', evening_end: '20:00' },
      tuesday: { enabled: true, start: '09:00', end: '14:00', break_start: '14:00', break_end: '16:00', evening_start: '16:00', evening_end: '20:00' },
      wednesday: { enabled: true, start: '09:00', end: '14:00', break_start: '14:00', break_end: '16:00', evening_start: '16:00', evening_end: '20:00' },
      thursday: { enabled: true, start: '09:00', end: '14:00', break_start: '14:00', break_end: '16:00', evening_start: '16:00', evening_end: '20:00' },
      friday: { enabled: true, start: '09:00', end: '14:00', break_start: '14:00', break_end: '16:00', evening_start: '16:00', evening_end: '20:00' },
      saturday: { enabled: false, start: '09:00', end: '14:00', break_start: '', break_end: '', evening_start: '', evening_end: '' },
      sunday: { enabled: false, start: '09:00', end: '14:00', break_start: '', break_end: '', evening_start: '', evening_end: '' },
    },
    slotDuration: 30,
    googleCalendarConnected: false,
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const { data, error } = await supabase
        .from('calendar_settings')
        .select('*');

      if (error) throw error;

      if (data && data.length > 0) {
        const settingsMap: Record<string, any> = {};
        data.forEach((item: Setting) => {
          settingsMap[item.setting_key] = item.setting_value;
        });

        setSettings({
          workingHours: settingsMap.workingHours || settings.workingHours,
          slotDuration: settingsMap.slotDuration || settings.slotDuration,
          googleCalendarConnected: settingsMap.googleCalendarConnected || false,
        });
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await supabase.from('calendar_settings').upsert([
        { setting_key: 'workingHours', setting_value: settings.workingHours },
        { setting_key: 'slotDuration', setting_value: settings.slotDuration },
        { setting_key: 'googleCalendarConnected', setting_value: settings.googleCalendarConnected },
      ], { onConflict: 'setting_key' });

      alert('Settings saved successfully!');
    } catch (error) {
      console.error('Error saving settings:', error);
      alert('Error saving settings');
    } finally {
      setSaving(false);
    }
  };

  const days = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

  if (loading) {
    return (
      <div className="text-center py-12">
        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h2 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
              <SettingsIcon className="text-blue-600" />
              Calendar Settings
            </h2>
            <p className="text-gray-600 mt-1">Configure appointment scheduling settings</p>
          </div>
        </div>
      </div>

      <div className="grid gap-6">
        <div className="bg-white rounded-lg shadow-md p-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
            <Clock className="text-blue-600" size={20} />
            Appointment Duration
          </h3>
          <div className="max-w-xs">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Default Slot Duration (minutes)
            </label>
            <input
              type="number"
              value={settings.slotDuration}
              onChange={(e) => setSettings({ ...settings, slotDuration: parseInt(e.target.value) })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              min="15"
              step="15"
            />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
            <Calendar className="text-blue-600" size={20} />
            Working Hours
          </h3>
          <div className="space-y-4">
            {days.map((day) => {
              const daySettings = settings.workingHours[day as keyof typeof settings.workingHours];
              return (
                <div key={day} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center gap-4 mb-3">
                    <input
                      type="checkbox"
                      checked={daySettings.enabled}
                      onChange={(e) => setSettings({
                        ...settings,
                        workingHours: {
                          ...settings.workingHours,
                          [day]: { ...daySettings, enabled: e.target.checked }
                        }
                      })}
                      className="w-4 h-4 text-blue-600 rounded"
                    />
                    <span className="font-medium text-gray-900 capitalize w-24">{day}</span>
                  </div>
                  {daySettings.enabled && (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 ml-8">
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">Morning Start</label>
                        <input
                          type="time"
                          value={daySettings.start}
                          onChange={(e) => setSettings({
                            ...settings,
                            workingHours: {
                              ...settings.workingHours,
                              [day]: { ...daySettings, start: e.target.value }
                            }
                          })}
                          className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">Morning End</label>
                        <input
                          type="time"
                          value={daySettings.end}
                          onChange={(e) => setSettings({
                            ...settings,
                            workingHours: {
                              ...settings.workingHours,
                              [day]: { ...daySettings, end: e.target.value }
                            }
                          })}
                          className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">Evening Start</label>
                        <input
                          type="time"
                          value={daySettings.evening_start}
                          onChange={(e) => setSettings({
                            ...settings,
                            workingHours: {
                              ...settings.workingHours,
                              [day]: { ...daySettings, evening_start: e.target.value }
                            }
                          })}
                          className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">Evening End</label>
                        <input
                          type="time"
                          value={daySettings.evening_end}
                          onChange={(e) => setSettings({
                            ...settings,
                            workingHours: {
                              ...settings.workingHours,
                              [day]: { ...daySettings, evening_end: e.target.value }
                            }
                          })}
                          className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                        />
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">Google Calendar Integration</h3>
          <p className="text-gray-600 mb-4">
            Connect your Google Calendar to sync appointments automatically.
          </p>
          <button className="bg-gray-200 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-300 transition-colors">
            Connect Google Calendar (Coming Soon)
          </button>
        </div>

        <div className="flex justify-end">
          <button
            onClick={handleSave}
            disabled={saving}
            className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2 disabled:opacity-50"
          >
            <Save size={20} />
            {saving ? 'Saving...' : 'Save Settings'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CalendarSettings;
